/**
 * Facility-level aggregates over persisted workflow tasks (localStorage).
 * Uses mock resident list for names/rooms; task data from TaskService.
 */

import { residents as mockResidents } from '../../data/mockData';
import { WORKFLOW_KEYS } from '../../data/workflowChecklistTemplates';
import { listTasks } from './TaskService';
import { TASK_STATUS } from '../../workflow/taskModel';

/** Admission + settling-in workflows tracked on the facility dashboard */
const SETTLING_WORKFLOW_KEYS = [
  WORKFLOW_KEYS.STAGE6_ADMISSION,
  WORKFLOW_KEYS.STAGE7A_SETTLING,
];

/**
 * @param {string} facilityId
 * @returns {import('../../workflow/taskModel').WorkflowTask[]}
 */
function allSettlingTasksForResident(residentId) {
  const out = [];
  for (const key of SETTLING_WORKFLOW_KEYS) {
    out.push(...listTasks(residentId, key));
  }
  return out;
}

/**
 * One row per resident in the facility who has at least one persisted workflow task
 * in Stage 6 or 7A (or show all residents with zero tasks — caller can filter).
 *
 * @param {string} facilityId
 * @param {{ includeWithoutTasks?: boolean }} [opts]
 */
export function getFacilitySettlingRows(facilityId, opts = {}) {
  const { includeWithoutTasks = false } = opts;
  const list = mockResidents.filter((r) => r.facility === facilityId);

  const rows = list.map((r) => {
    const tasks = allSettlingTasksForResident(r.id);
    const completed = tasks.filter((t) => t.status === TASK_STATUS.COMPLETED).length;
    const total = tasks.length;
    const percent = total ? Math.round((completed / total) * 100) : 0;
    const blockers = tasks.filter((t) => t.status === TASK_STATUS.BLOCKED);
    const overdue = tasks.filter((t) => t.status === TASK_STATUS.OVERDUE);
    return {
      residentId: r.id,
      name: r.name,
      room: r.room,
      admissionDate: r.admissionDate,
      percent,
      total,
      completed,
      blockerCount: blockers.length,
      overdueCount: overdue.length,
      blockerLabels: blockers.slice(0, 3).map((t) => t.title),
    };
  });

  if (!includeWithoutTasks) {
    return rows.filter((row) => row.total > 0);
  }
  return rows;
}
